import { api } from './client';

export interface DocumentRequest {
  id: string;
  legacyId: number | null;
  clientId: number | null;
  clientName: string;
  task: string;
  comment: string | null;
  activeFrom: string | null;
  copies: string | null;
  attach: string | null;
  isDone: boolean;
  isArchived: boolean;
  status: 'NEW' | 'IN_PROGRESS' | 'READY_TO_PRINT' | 'ARCHIVED';
  completedAt: string | null;
  archivedAt: string | null;
  createdBy: { id: string; name: string | null } | null;
  legacyCreatedByName: string | null;
  legacyLastChange: string | null;
  legacyLog: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentRequestAuditLog {
  id: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  modelName: string;
  recordId: string;
  userId: string | null;
  userName: string | null;
  dataBefore: Record<string, unknown> | null;
  dataAfter: Record<string, unknown> | null;
  diff: Record<string, { old: unknown; new: unknown }> | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  attachment?: DocumentRequestAttachment | null;
}

export interface DocumentRequestAttachment {
  id: string;
  requestId: string;
  originalName: string;
  mimeType: string;
  extension: string;
  sizeBytes: number;
  sha256: string;
  kind: 'DOCUMENT' | 'IMAGE' | 'VIDEO';
  sourceApp: 'DESKTOP' | 'MOBILE';
  uploadedBy: { id: string; name: string | null; email?: string | null } | null;
  deletedAt: string | null;
  deletedBy: { id: string; name: string | null; email?: string | null } | null;
  createdAt: string;
  canPreview: boolean;
  canDownload: boolean;
  canDelete: boolean;
}

export interface CreateDocumentRequestDto {
  clientId: number;
  task: string;
  comment?: string | null;
}

export interface UpdateDocumentRequestDto {
  task?: string;
  comment?: string | null;
  activeFrom?: string | null;
  status?: DocumentRequest['status'];
}

export const documentRequestsApi = {
  getByClient: async (clientId: number, archive: 0 | 1 = 0) => {
    const { data } = await api.get(`/document-requests/client/${clientId}`, {
      params: { isArchived: archive === 1, status: archive === 1 ? 'archived' : 'active' },
    });
    return (data.items || []) as DocumentRequest[];
  },

  create: async (dto: CreateDocumentRequestDto) => {
    const { data } = await api.post('/document-requests', dto);
    return data as DocumentRequest;
  },

  update: async (id: string, dto: UpdateDocumentRequestDto) => {
    const { data } = await api.patch(`/document-requests/${id}`, dto);
    return data as DocumentRequest;
  },

  getHistory: async (id: string) => {
    const { data } = await api.get(`/document-requests/${id}/history`);
    return data as { items: DocumentRequestAuditLog[]; total: number };
  },

  getAttachments: async (id: string) => {
    const { data } = await api.get(`/document-requests/${id}/attachments`);
    return data as { items: DocumentRequestAttachment[] };
  },

  uploadAttachments: async (
    id: string,
    files: File[],
    sourceApp: 'DESKTOP' | 'MOBILE' = 'MOBILE',
  ) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('sourceApp', sourceApp);
    const { data } = await api.post(`/document-requests/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data as {
      uploaded: DocumentRequestAttachment[];
      errors: Array<{ filename: string; message: string }>;
    };
  },

  downloadAttachment: async (id: string, attachmentId: string) => {
    const { data } = await api.get(
      `/document-requests/${id}/attachments/${attachmentId}/download`,
      { responseType: 'blob' },
    );
    return data as Blob;
  },

  previewAttachment: async (id: string, attachmentId: string) => {
    const { data } = await api.get(
      `/document-requests/${id}/attachments/${attachmentId}/preview`,
      { responseType: 'blob' },
    );
    return data as Blob;
  },

  deleteAttachment: async (id: string, attachmentId: string) => {
    const { data } = await api.delete(`/document-requests/${id}/attachments/${attachmentId}`);
    return data as { success: boolean };
  },
};
